import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import Button from './Button';
import { cn } from '@/lib/cn';

type Tone = 'danger' | 'warning' | 'info';

interface Props {
  open: boolean;
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export default function ConfirmDialog({
  open, title, message,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  tone = 'danger',
  onConfirm, onCancel,
}: Props) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
      if (e.key === 'Enter'  && !busy) handleConfirm();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, busy]);

  async function handleConfirm() {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  const iconColor = {
    danger:  'text-red-400',
    warning: 'text-orange-400',
    info:    'text-accent-400',
  }[tone];

  const buttonVariant: 'danger' | 'primary' = tone === 'danger' ? 'danger' : 'primary';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={() => !busy && onCancel()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border border-slate-800 bg-bg-card shadow-2xl"
      >
        <div className="flex items-start gap-3 p-5">
          <div className={cn('shrink-0 mt-0.5', iconColor)}>
            <AlertTriangle size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-muted-strong">{title}</h3>
            {message && <div className="text-sm text-muted mt-1.5 leading-relaxed">{message}</div>}
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-muted hover:text-muted-strong p-1 -m-1 rounded disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-800 bg-slate-900/30 rounded-b-xl">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={buttonVariant} size="sm" onClick={handleConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
